'use client';

import { useCallback, useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch';

import { type ProductHit, transformHitForProductTile } from '@libs/algolia';
import { type Product } from '@libs/common-next';

import AlgoliaNoResultsBoundary from '@/components/AlgoliaNoResultsBoundary';
import ProductGrid from '@/components/ProductGrid/ProductGrid';

import type { ProductListProps } from './types';

export default function ProductList({ entryId, forceCategoryH1, seoMetadata, isCategoryActive }: ProductListProps) {
    const [stringifiedProductIds, setStringifiedProductIds] = useState<string>('');
    const [productsToShow, setProductsToShow] = useState<Product[]>([]);
    const [queryId, setQueryId] = useState<string>('first_query');

    const transformItems = useCallback(
        (items: ProductHit[]) => items.map((item: ProductHit) => transformHitForProductTile(item, true)),
        []
    );

    const stringifiyIds = (items: ProductHit[]) => items.map((p: ProductHit) => p.id).join(',');

    const { items, results } = useHits({ transformItems });

    const products = (stringifiedProductIds !== '' ? productsToShow : items) as Product[];

    // This is a hack to only update hits to be displayed if they have ACTUALLY changed. When filters change in InstantSearch the Algolia hook first returns the old list
    // then it updates with the new list a few seconds later, seems like a bug with Algolia
    //
    // We also refresh when the `queryID` changes even if the product IDs are
    // identical. The first (SSR / token-gated replay) query has analytics
    // disabled and returns hits without a `__queryID`; a subsequent tracked
    // query returns the same products but now carrying a `__queryID`. Without
    // the queryID check we'd keep showing the stale, un-attributable hits and
    // product clicks would never fire the Algolia click event.
    useEffect(() => {
        const newStringifiedIds = stringifiyIds(items);
        const newQueryId = results?.queryID ?? '';
        const shouldRefreshProductsToShow = newStringifiedIds !== stringifiedProductIds || newQueryId !== queryId;

        if (shouldRefreshProductsToShow) {
            setStringifiedProductIds(newStringifiedIds);
            setProductsToShow(items as Product[]);
            setQueryId(newQueryId);
        }
    }, [items, results?.queryID]);

    return (
        <AlgoliaNoResultsBoundary products={products} isCategoryActive={isCategoryActive}>
            <ProductGrid
                entryId={entryId}
                products={products}
                totalResults={results ? results.nbHits : 0}
                queryid={queryId}
                forceCategoryH1={forceCategoryH1}
                seoMetadata={seoMetadata}
            />
        </AlgoliaNoResultsBoundary>
    );
}
